/**
 * Groups Store
 *
 * Zustand store for the Groups UI state that lives ONLY on the client:
 *   - which groups are currently collapsed in the tree
 *
 * Persistence: localStorage key `ra_groups_collapsed_v1`. The `_v1` suffix
 * lets us bump the schema (rename, change shape) without breaking existing
 * clients — just check the version on load and migrate / discard if needed.
 *
 * Server-owned state (the actual group/subgroup list, agent assignments)
 * lives in TanStack Query via `useGroups`. Do NOT mirror it here.
 */

import { create } from 'zustand';

const STORAGE_KEY = 'ra_groups_collapsed_v1';

const loadCollapsed = (): string[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x): x is string => typeof x === 'string');
  } catch {
    return [];
  }
};

const saveCollapsed = (ids: string[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // ignore storage errors (quota, private mode, etc.)
  }
};

interface GroupsState {
  collapsedIds: Set<string>;

  isCollapsed: (id: string) => boolean;
  toggleCollapse: (id: string) => void;
  expandAll: () => void;
  collapseAll: (ids: string[]) => void;
}

export const useGroupsStore = create<GroupsState>((set, get) => ({
  collapsedIds: new Set(loadCollapsed()),

  isCollapsed: (id) => get().collapsedIds.has(id),

  toggleCollapse: (id) =>
    set((state) => {
      const next = new Set(state.collapsedIds);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      saveCollapsed(Array.from(next));
      return { collapsedIds: next };
    }),

  expandAll: () => {
    saveCollapsed([]);
    set({ collapsedIds: new Set() });
  },

  collapseAll: (ids) => {
    const next = new Set(ids);
    saveCollapsed(Array.from(next));
    set({ collapsedIds: next });
  },
}));
